import {PreventionInvalidityCode} from "./invalidity-codes";
import {PreventativeAutocompleteType} from "./field-types";

/**
 * @typedef {Object} PreventativeField
 * @property {string}   key            - Matches a key in the raw parsedRow
 * @property {string}   [label]        - Column header label in the DataGrid
 * @property {string}   type           - UI control id, e.g. part-autocomplete, toggle, input, text
 * @property {Object}   [autocompleteProps] - Props for the autocomplete component
 * @property {Object}   [inputProps]   - Props for the input component
 * @property {Object}   [textFieldProps] - Props for TextField (e.g. number input)
 * @property {Function} [getOptions]   - async (cancelToken, keywords, staticDataContext) => options[]
 * @property {Function} [extractValue]   - (rawAutocompleteObject) => scalar string/number
 * @property {Function} modifyRow      - (parsedRow, newFieldValue) => updatedParsedRow
 * @property {boolean}  [modifyRowUsesPatchValue] - When true, modifyRow receives the raw patch (e.g. autocomplete object) instead of extractValue output
 */

/**
 * @typedef {Object} PreventativeSectionConfig
 * @property {string}   sectionKey        - Unique key, e.g. 'invalidPart'
 * @property {string}   sectionTitle      - Section heading in the dialog
 * @property {string}   invalidityCode    - Token the transformer stamps on bad rows (e.g. 'INVALID_PART')
 * @property {string[]} [affectedColumns]   - Which mapped-field keys this section addresses
 * @property {PreventativeField[]} fields - UI controls to show for this invalidity
 */

export const createInvalidPartSection = (key = 'partNumber') => ({
    sectionKey: 'invalidPart',
    sectionTitle: 'Invalid Part Numbers',
    invalidityCode: PreventionInvalidityCode.INVALID_PART,
    fields: [
        {
            key: key,
            label: 'Part Number',
            type: PreventativeAutocompleteType.PART,
            extractValue: (o) => o?.partNo ?? null,
            modifyRow: (row, val) => ({...row, [key]: val}),
            autocompleteProps: {
                placeholder: 'Search for part...',
                getOptionLabel: (o) => o?.partNo ?? '',
                isOptionEqualToValue: (o, v) => o?.partNo === v?.partNo,
            },
        }
    ],
});

export const createInvalidExternalPartSection = (key = 'partNumber') => ({
    sectionKey: 'invalidExternalPart',
    sectionTitle: 'Invalid Part Numbers',
    invalidityCode: PreventionInvalidityCode.INVALID_PART,
    fields: [
        {
            key: key,
            label: 'Part Number',
            type: PreventativeAutocompleteType.EXTERNAL_PART,
            extractValue: (o) => o?.partNo ?? null,
            modifyRow: (row, val) => ({...row, [key]: val}),
            autocompleteProps: {
                placeholder: 'Search for part...',
                getOptionLabel: (o) => o?.partNo ?? '',
                isOptionEqualToValue: (o, v) => o?.partNo === v?.partNo,
            },
        }
    ],
});

export const createInvalidVendorSection = (key = 'vendorCode') => ({
    sectionKey: 'invalidVendor',
    sectionTitle: 'Invalid Vendor Codes',
    invalidityCode: PreventionInvalidityCode.INVALID_VENDOR,
    fields: [{
        key: key,
        label: 'Vendor Code',
        type: PreventativeAutocompleteType.VENDOR,
        extractValue: (o) => o?.code ?? null,
        modifyRow: (row, val) => ({...row, [key]: val})
    }]
});


export const createInvalidCustomerCodeSection = (key = 'customerNo') => ({
    sectionKey: 'invalidCustomer',
    sectionTitle: 'Invalid Customer Codes',
    invalidityCode: PreventionInvalidityCode.INVALID_CUSTOMER,
    fields: [{
        key: key,
        label: 'Customer',
        type: PreventativeAutocompleteType.CUSTOMER,
        extractValue: (o) => o?.code ?? null,
        modifyRow: (row, val) => ({...row, [key]: val}),
        autocompleteProps: {
            placeholder: 'Search for customer...',
            getOptionLabel: (o) => o?.name ? `${o.name} (${o.code})` : o?.code ?? '',
            isOptionEqualToValue: (o, v) => o?.code === v?.code,
        },
    }]
});

export const createInvalidExternalCustomerCodeSection = (key = 'customerCode') => ({
    sectionKey: 'invalidExternalCustomer',
    sectionTitle: 'Invalid Customer Code',
    invalidityCode: PreventionInvalidityCode.INVALID_CUSTOMER,
    fields: [{
        key: key,
        label: 'External Customer',
        type: PreventativeAutocompleteType.EXTERNAL_CUSTOMER,
        extractValue: (o) => o?.customerNo ?? null,
        modifyRow: (row, val) => ({...row, [key]: val}),
        autocompleteProps: {
            placeholder: 'Search for customer...',
            getOptionLabel: (o) => o?.name ? `${o.name} (${o.customerNo})` : o?.customerNo ?? '',
            isOptionEqualToValue: (o, v) => o?.customerNo === v?.customerNo,
        },
    }]
});

export const createPartnerAutocompleteField = (key = 'partnerName') => ({
    key,
    label: 'Partner',
    type: PreventativeAutocompleteType.PARTNER,
    modifyRowUsesPatchValue: true,
    extractValue: (o) => (typeof o === 'string' ? o : o?.sellerInfo?.name ?? null),
    modifyRow: (row, val) => ({
        ...row,
        partnerName: typeof val === 'string' ? val : val?.sellerInfo?.name ?? val,
        partnerConnectionId:
            typeof val === 'object' && val?.id != null
                ? String(val.id)
                : row.partnerConnectionId,
    }),
    autocompletePropsFromContext: (staticDataContext) => ({
        cachedPartners: staticDataContext?.approvedPartners ?? [],
        getOptionLabel: (o) => o?.sellerInfo?.description ?? o?.sellerInfo?.name ?? o?.sellerInfo?.id ?? '',
        isOptionEqualToValue: (o, v) => o?.id === v?.id || o?.sellerInfo?.name === v?.sellerInfo?.name,
    }),
});

export const createInvalidPartnerSection = (key = 'partnerName') => ({
    sectionKey: 'invalidPartner',
    sectionTitle: 'Invalid Partner Names',
    invalidityCode: PreventionInvalidityCode.INVALID_PARTNER,
    fields: [createPartnerAutocompleteField(key)],
});

export const createFormAutocompleteField = (key = 'formTitle') => ({
    key,
    label: 'Form Title',
    type: PreventativeAutocompleteType.FORM,
    modifyRowUsesPatchValue: true,
    extractValue: (o) => (typeof o === 'string' ? o : o?.title ?? null),
    modifyRow: (row, val) => ({
        ...row,
        formTitle: typeof val === 'string' ? val : val?.title ?? val,
    }),
    autocompletePropsFromContext: (staticDataContext) => ({
        api: staticDataContext?.formsApi,
        cachedForms: staticDataContext?.allForms ?? [],
        freeSolo: false,
        getOptionLabel: (o) => o?.title ?? '',
        isOptionEqualToValue: (o, v) =>
            typeof o === 'string' || typeof v === 'string' ? o === v : o?.id === v?.id,
    }),
});

export const createInvalidFormSection = (key = 'formTitle') => ({
    sectionKey: 'invalidForm',
    sectionTitle: 'Invalid Form Titles',
    invalidityCode: PreventionInvalidityCode.INVALID_FORM,
    fields: [createFormAutocompleteField(key)],
});

export const createProductRelationAutocompleteField = (key = 'relationTitle') => ({
    key,
    label: 'Relation Title',
    type: PreventativeAutocompleteType.PRODUCT_RELATION,
    modifyRowUsesPatchValue: true,
    extractValue: (o) => (typeof o === 'string' ? o : o?.title ?? null),
    modifyRow: (row, val) => ({
        ...row,
        relationTitle: typeof val === 'string' ? val : val?.title ?? val,
    }),
    autocompletePropsFromContext: (staticDataContext) => ({
        cachedRelationTypes: staticDataContext?.allProductRelationTypes ?? [],
        freeSolo: false,
        getOptionLabel: (o) => o?.title ?? '',
        isOptionEqualToValue: (o, v) =>
            typeof o === 'string' || typeof v === 'string' ? o === v : o?.id === v?.id,
    }),
});

export const createInvalidRelationSection = (key = 'relationTitle') => ({
    sectionKey: 'invalidRelation',
    sectionTitle: 'Invalid Relation Titles',
    invalidityCode: PreventionInvalidityCode.INVALID_RELATION,
    fields: [createProductRelationAutocompleteField(key)],
});

export const createInvalidTagSection = (key = 'tagName') => ({
    sectionKey: 'invalidTag',
    sectionTitle: 'Invalid Tag Names',
    invalidityCode: PreventionInvalidityCode.INVALID_TAG,
    fields: [{
        key: key,
        label: 'Tag Name',
        type: PreventativeAutocompleteType.TAG,
        extractValue: (o) => (typeof o === 'string' ? o : o?.name ?? null),
        modifyRow: (row, val) => ({
            ...row,
            [key]: typeof val === 'string' ? val : val?.name ?? val,
        }),
        autocompletePropsFromContext: (staticDataContext) => ({
            industryId: staticDataContext?.industryId,
            freeSolo: false,
            getOptionLabel: (o) => o?.name ?? '',
            isOptionEqualToValue: (o, v) =>
                typeof o === 'string' || typeof v === 'string'
                    ? o === v
                    : o?.id === v?.id,
        }),
    }],
});

export const createInvalidWarehouseSection = (key = 'warehouseCode') => ({
    sectionKey: 'invalidWarehouse',
    sectionTitle: 'Invalid Warehouse Codes',
    invalidityCode: PreventionInvalidityCode.INVALID_WAREHOUSE,
    fields: [{
        key: key,
        label: 'Warehouse',
        type: PreventativeAutocompleteType.WAREHOUSE,
        extractValue: (o) => o?.code ?? null,
        modifyRow: (row, val) => ({...row, [key]: val}),
        autocompleteProps: {
            placeholder: 'Search for warehouse...',
            getOptionLabel: (o) => o?.name ? `${o.name} (${o.code})` : o?.code ?? '',
            isOptionEqualToValue: (o, v) => o?.code === v?.code,
        },
    }]
});

export const createBooleanField = (key, label) => ({
    key,
    label,
    type: 'toggle',
    modifyRow: (row, val) => ({...row, [key]: val})
});
